import I18n from 'I18n';
import { useMemo } from 'react';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { AVAILABLE, AWAY, BUSY, INACTIVE } from '../constants/agent-state';
import { isHumanAgent } from '../operators/assignees';
import { useIsUngatedForCapacityCallStatus, useIsUngatedForUserLevelCapacityLimits, useIsUngatedForUserLevelCapacityLimitsTicketType } from '../../auth/hooks/useGates';
import { isAgentOnCall } from '../../agent-status/utils/capacityUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getAgentCapacityMessage = (agent, isUngatedForCapacityCallStatus) => {
  if (!isHumanAgent(agent)) {
    return null;
  }
  if (isAgentOnCall(agent) && isUngatedForCapacityCallStatus) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "routing-ui.reassignment.capacityStatus.onACall"
    });
  }
  switch (agent.capacityStatus) {
    case 'FULL':
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "routing-ui.reassignment.capacityStatus.fullCapacity"
        }), /*#__PURE__*/_jsx(HelpIcon, {
          alignWithText: true,
          marginInlineStart: "100",
          title: I18n.text('routing-ui.reassignment.capacityStatus.tooltip.fullCapacity')
        })]
      });
    case 'OVER':
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "routing-ui.reassignment.capacityStatus.overCapacity"
        }), /*#__PURE__*/_jsx(HelpIcon, {
          alignWithText: true,
          marginInlineStart: "100",
          title: I18n.text('routing-ui.reassignment.capacityStatus.tooltip.overCapacity')
        })]
      });
    default:
      return null;
  }
};
const getAgentAvailabilityMessage = ({
  agentState,
  agent
}) => {
  if (!isHumanAgent(agent)) {
    return null;
  }
  if (agent.isOutOfOffice && agent.outOfOfficeEndsAt) {
    const returnTime = I18n.moment(agent.outOfOfficeEndsAt);
    const format = returnTime.isSame(new Date(), 'day') ? 'LT' : 'short-date'; // LT -> 9:30 PM, short-date -> Apr 23
    return /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "routing-ui.reassignment.statusTypes.OUT_OF_OFFICE",
      options: {
        dateOrTime: /*#__PURE__*/_jsx(FormattedDateTime, {
          type: "userTz",
          value: returnTime.valueOf(),
          format: format
        })
      }
    });
  }
  if (agentState === AWAY && !agent.isInWorkingHours) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "routing-ui.reassignment.statusTypes.AWAY_OUTSIDE_WH"
    });
  }
  switch (agentState) {
    case AVAILABLE:
    case BUSY:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "routing-ui.reassignment.statusTypes.AVAILABLE"
      });
    case AWAY:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "routing-ui.reassignment.statusTypes.AWAY"
      });
    case INACTIVE:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "routing-ui.reassignment.statusTypes.INACTIVE"
      });
    default:
      return null;
  }
};
export const useAgentStatusMessage = ({
  responder,
  showCapacityStatus = true
}) => {
  const hasCapacityStatus = useIsUngatedForUserLevelCapacityLimits();
  const isUngatedForCapacityTicketType = useIsUngatedForUserLevelCapacityLimitsTicketType();
  const isUngatedForCapacityCallStatus = useIsUngatedForCapacityCallStatus();
  const statusMessage = useMemo(() => {
    const availabilityStatus = getAgentAvailabilityMessage(responder);
    const canShowCapacityStatus = !isUngatedForCapacityTicketType || showCapacityStatus;
    const capacityStatus = hasCapacityStatus && canShowCapacityStatus && getAgentCapacityMessage(responder.agent, isUngatedForCapacityCallStatus);
    if (availabilityStatus && capacityStatus) {
      return /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "routing-ui.reassignment.agentStatusMessage.twoStatuses",
        options: {
          availabilityStatus,
          capacityStatus
        }
      });
    }
    return availabilityStatus || capacityStatus;
  }, [responder, hasCapacityStatus, isUngatedForCapacityTicketType, showCapacityStatus, isUngatedForCapacityCallStatus]);
  return statusMessage;
};